import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { gridSize } from '@atlaskit/theme';

import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import { PrivacyPolicyLink, UserNoticeLink } from '../components/links';
import { isMobileOidc } from '../utilities/oidc/oidc';
import Account from '../components/Account';
import messages from './LogoutPage.messages';
import Bull from '../components/BulletSeparator';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { SectionError } from '../components/FormError';
import { useLocation } from 'react-router-dom';
import { doLogout } from '../actions/session-actions';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import Heading from '@atlaskit/heading';

const AccountWrapper = styled.div`
  > * {
    padding-bottom: ${gridSize()}px !important;
    border-bottom: none !important;
  }
`;

const Form = styled.form`
  margin-top: 0;
`;

const ButtonContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const pageId = 'logoutPage';

export async function logoutLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/logout/state', request, {
    capabilities: ['capability-group--logout'],
  });
}

export const LogoutPage = () => {
  const autoSubmit = useAppSelector(state => state.logout.autoSubmit);
  return autoSubmit ? <LogoutAutoSubmit /> : <LogoutConfirmationPage />;
};

const LogoutAutoSubmit = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(doLogout());
  }, [dispatch]);

  // When the browser loads the logout page the user will see the spinner on that page so we don't
  // want to show anything on this page, not even a spinner, to make the redirect more transparent.
  return null;
};

const LogoutConfirmationPage = () => {
  const { isEmbedded, oidcContext } = useAppSelector(state => state.microbranding);
  const { userData, formError } = useAppSelector(state => state.logout);

  const location = useLocation();
  const intl = useIntl();
  const analyticsClient = useAnalyticsClient();

  usePageViewedEvent(pageId, {
    isAuthenticatedUser: !!userData,
  });

  const buildHeader = () => (
    <Heading as="h1" size="small">
      {!userData || userData.associatedSessions.length === 0 ? (
        <FormattedMessage id="logout.title" defaultMessage="Log out of your Atlassian account" />
      ) : (
        <FormattedMessage
          id="logout.title.multiple"
          defaultMessage="Log out of your Atlassian accounts"
        />
      )}
    </Heading>
  );

  const buildFooter = () => {
    return isEmbedded ? undefined : (
      <ul className="fine-print">
        <li>
          <PrivacyPolicyLink isMobileLink={isMobileOidc(oidcContext)} />
        </li>
        <Bull />
        <li>
          <UserNoticeLink isMobileLink={isMobileOidc(oidcContext)} />
        </li>
      </ul>
    );
  };

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.logoutTitle)}</title>
      </Helmet>
      <WhiteboxPage
        pageId={pageId}
        header={buildHeader()}
        footer={buildFooter()}
        whiteboxFooter={
          <a
            href={'/login/select-account' + location.search}
            onClick={() => {
              analyticsClient.linkClickedEvent(pageId, 'selectAccount');
            }}>
            <FormattedMessage
              id="logout.switch-account"
              defaultMessage="Log in to another account"
            />
          </a>
        }>
        {!!formError && <SectionError>{formError}</SectionError>}
        {!!userData && userData.associatedSessions.length === 0 && (
          <AccountWrapper>
            <Account
              primary={true}
              avatarUrl={userData.avatarUrl}
              displayName={userData.displayName}
              email={userData.email}
              isolatedCloudLabel={userData.icLabel}
            />
          </AccountWrapper>
        )}
        <LogoutForm />
      </WhiteboxPage>
    </>
  );
};

const LogoutForm = () => {
  const userData = useAppSelector(state => state.logout.userData);
  const dispatch = useAppDispatch();

  const analyticsClient = useAnalyticsClient();
  const [submitting, setSubmitting] = useState(false);

  return (
    <Form
      onSubmit={event => {
        event.preventDefault();
        setSubmitting(true);
        analyticsClient.formSubmittedEvent(pageId, 'logout');
        dispatch(doLogout());
      }}
      data-testid="logout-form">
      <ButtonContainer>
        <TallButton
          appearance="primary"
          id="logout-submit"
          type="submit"
          isFullWidth
          isLoading={submitting}
          testId="logout-button">
          {!userData || userData.associatedSessions.length === 0 ? (
            <FormattedMessage id="logout.confirm" defaultMessage="Log out" />
          ) : (
            <FormattedMessage
              id="logout.confirm.multiple"
              defaultMessage="Log out of all accounts"
            />
          )}
        </TallButton>
      </ButtonContainer>
    </Form>
  );
};

export default LogoutPage;
